import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Footer } from "../components/Footer";
import { getProductBySlug, ALL_PRODUCTS } from "../data/products";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Accordion = ({ title, testId, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-[#E5E5E5]" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <h2 className="font-playfair text-xl md:text-2xl text-[#1A1A1A] group-hover:text-[#3d7d83] transition-colors duration-300">
          {title}
        </h2>
        <ChevronDown
          size={22}
          className={`text-[#C5A059] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pb-6 text-[#666666] leading-relaxed whitespace-pre-line">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const Header = () => (
  <header className="glass-nav py-4" data-testid="product-header">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between">
      <Link
        to="/"
        data-testid="product-logo-home"
        className="font-logo text-3xl md:text-4xl leading-none text-[#1A1A1A]"
      >
        Urban Beauty
      </Link>
      <Link
        to="/products"
        data-testid="product-back-shop"
        className="flex items-center gap-2 text-sm tracking-wider uppercase font-medium text-[#1A1A1A] hover:text-[#C5A059] transition-colors duration-300"
      >
        <ArrowLeft size={16} />
        Zum Shop
      </Link>
    </div>
  </header>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const [businessInfo, setBusinessInfo] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/business-info`).then((res) => setBusinessInfo(res.data)).catch(() => {});
  }, [slug]);

  const related = product
    ? ALL_PRODUCTS.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 4)
    : [];

  return (
    <div className="bg-[#F9F8F6] min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-32 pb-24 px-6 md:px-12 lg:px-24">
        {!product ? (
          <div className="max-w-3xl mx-auto text-center" data-testid="product-not-found">
            <h1 className="font-playfair text-4xl text-[#1A1A1A] mb-4">Produkt nicht gefunden</h1>
            <p className="text-[#666666] mb-8">Dieses Produkt ist nicht verfügbar.</p>
            <Link to="/products" className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#3d7d83] transition-colors">
              <ArrowLeft size={16} /> Zurück zum Shop
            </Link>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <nav className="text-sm text-[#999] mb-8 flex items-center gap-2" data-testid="product-breadcrumb">
              <Link to="/products" className="hover:text-[#C5A059] transition-colors">Shop</Link>
              <span>/</span>
              <span className="text-[#666666]">{product.category}</span>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start"
            >
              {/* Image */}
              <div
                className="bg-white border border-[#E5E5E5] border-t-2 border-t-[#3d7d83] aspect-square flex items-center justify-center p-8 md:sticky md:top-28"
                data-testid="product-detail-image"
              >
                <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
              </div>

              {/* Info */}
              <div>
                <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-3">{product.category}</p>
                <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight" data-testid="product-detail-name">
                  {product.name}
                </h1>
                <p className="font-playfair text-2xl text-[#C5A059] mt-4" data-testid="product-detail-price">
                  {product.price}
                  <span className="ml-2 text-xs tracking-wider uppercase text-[#999] font-sans align-middle">UVP</span>
                </p>
                <p className="text-xs text-[#999] mt-1">Unverbindliche Preisempfehlung</p>

                <div className="w-16 h-[1px] bg-gradient-to-r from-[#C5A059] to-[#3d7d83] my-8" />

                {/* Collapsible sections */}
                <div className="border-t border-[#E5E5E5]">
                  <Accordion title="Beschreibung" testId="product-section-beschreibung">
                    {product.description
                      ? product.description
                      : "Detaillierte Produktbeschreibung folgt in Kürze. Gerne beraten wir Sie persönlich in unserem Studio."}
                  </Accordion>

                  {product.inci && (
                    <Accordion title="Inhaltsstoffe" testId="product-section-inhaltsstoffe">
                      {product.inci}
                    </Accordion>
                  )}

                  {product.anwendung && (
                    <Accordion title="Anwendung" testId="product-section-anwendung">
                      {product.anwendung}
                    </Accordion>
                  )}
                </div>

                {/* Studio note + contact */}
                <div className="mt-12 bg-white border border-[#E5E5E5] p-6">
                  <p className="text-[#1A1A1A] font-cormorant italic text-xl mb-2">
                    In unserem Studio in Klosters erhältlich
                  </p>
                  <p className="text-[#666666] text-sm mb-5">
                    Für eine persönliche Beratung oder Reservierung kontaktieren Sie uns gerne.
                  </p>
                  <Link
                    to="/#contact"
                    data-testid="product-contact-cta"
                    className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-6 py-3 rounded-full text-sm tracking-wider uppercase font-medium hover:bg-[#C5A059] hover:text-[#1A1A1A] transition-colors duration-300"
                  >
                    Kontakt aufnehmen <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Related products */}
            {related.length > 0 && (
              <div className="mt-24" data-testid="product-related">
                <h2 className="font-playfair text-2xl md:text-3xl text-[#1A1A1A] mb-8">Weitere Produkte aus {product.category}</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
                  {related.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/products/${item.slug}`}
                      data-testid={`related-item-${item.slug}`}
                      className="group bg-white border border-[#E5E5E5] hover:border-[#C5A059] transition-colors duration-300"
                    >
                      <div className="aspect-square flex items-center justify-center p-4">
                        <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="p-4 border-t border-[#F0EEE9]">
                        <p className="text-[#1A1A1A] text-sm leading-snug">{item.name}</p>
                        <p className="font-playfair text-[#C5A059] mt-1">{item.price}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <Footer businessInfo={businessInfo} />
    </div>
  );
}
